import React, { useEffect, useRef, useState } from "react";
import { Field } from "react-querybuilder";
import { IXSifterQuery, IXSifterQueryRoot, IXSifterQueryRule } from "../../types/XSifterQueries";
import { Box, Button, IconButton, Menu, MenuItem, Select, SelectChangeEvent, Stack, TextField } from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import "./QueryBuilderComponent.css"
import SwitchButton from "../common/SwitchButton";
import { checkStripNoRetweets } from "../common/QueryFunctions";

export const fields: Field[] = [
    { name: 'keyword', label: 'Keyword', operators: [
        { name: '=', label: 'is' },
    ] },
    { name: 'phrase', label: 'Phrase', operators: [
        { name: '=', label: 'is' },
    ] },
    { name: 'hashtag', label: 'hashtag #', operators: [
        { name: '=', label: 'is' },
    ] },
    { name: 'mention', label: 'mention @', operators: [
        { name: '=', label: 'is' },
    ] },
    { name: 'from', label: 'From', operators: [
        { name: '=', label: 'is' },
    ] },
    { name: 'to', label: 'To', operators: [
        { name: '=', label: 'contains' },
    ] },
    { name: 'url', label: 'URL', operators: [
        { name: '=', label: 'is' },
    ] },
    { name: 'has', label: 'Has', valueEditorType: 'checkbox', operators: [
        { name: 'hashtags', label: 'hashtags' },
        { name: 'images', label: 'images' },
        { name: 'links', label: 'links' },
        { name: 'media', label: 'media'},
        { name: 'mentions', label: 'mentions' },
        { name: 'videos', label: 'videos' },
    ] },
    { name: 'is', label: 'Is', valueEditorType: 'checkbox', operators: [
        { name: 'reply', label: 'reply' },
        { name: 'retweet', label: 'retweet' },
        { name: 'quote', label: 'quote' },
        { name: 'verified', label: 'verified' },
    ] },
    { name: 'retweetsOf', label: 'Retweets of', operators: [
        { name: '=', label: 'user posts' },
    ]},
    { name: 'lang', label: 'Language', valueEditorType: 'checkbox', operators: [
        { name: 'en', label: 'English' },
        { name: 'am', label: 'Amharic' },
        { name: 'ar', label: 'Arabic' },
        { name: 'hy', label: 'Armenian' },
        { name: 'eu', label: 'Basque' },
        { name: 'bn', label: 'Bengali' },
        { name: 'bs', label: 'Bosnian' },
        { name: 'bg', label: 'Bulgarian' },
        { name: 'my', label: 'Burmese' },
        { name: 'ca', label: 'Catalan' },
        { name: 'hr', label: 'Croatian' },
        { name: 'cs', label: 'Czech' },
        { name: 'da', label: 'Danish' },
        { name: 'nl', label: 'Dutch' },
        { name: 'et', label: 'Estonian' },
        { name: 'fi', label: 'Finnish' },
        { name: 'fr', label: 'French' },
        { name: 'ka', label: 'Georgian' },
        { name: 'de', label: 'German' },
        { name: 'el', label: 'Greek' },
        { name: 'gu', label: 'Gujarati' },
        { name: 'ht', label: 'Haitian Creole' },
        { name: 'iw', label: 'Hebrew' },
        { name: 'hi', label: 'Hindi' },
        { name: 'hu', label: 'Hungarian' },
        { name: 'is', label: 'Icelandic' },
        { name: 'in', label: 'Indonesian' },
        { name: 'it', label: 'Italian' },
        { name: 'ja', label: 'Japanese' },
        { name: 'kn', label: 'Kannada' },
        { name: 'km', label: 'Khmer' },
        { name: 'ko', label: 'Korean' },
        { name: 'lo', label: 'Lao' },
        { name: 'hi-Latn', label: 'Latinized Hindi' },
        { name: 'lv', label: 'Latvian' },
        { name: 'lt', label: 'Lithuanian' },
        { name: 'ml', label: 'Malayalam' },
        { name: 'dv', label: 'Maldivian' },
        { name: 'mr', label: 'Marathi' },
        { name: 'ne', label: 'Nepali' },
        { name: 'no', label: 'Norwegian' },
        { name: 'or', label: 'Oriya' },
        { name: 'pa', label: 'Panjabi' },
        { name: 'ps', label: 'Pashto' },
        { name: 'fa', label: 'Persian' },
        { name: 'pl', label: 'Polish' },
        { name: 'pt', label: 'Portuguese' },
        { name: 'ro', label: 'Romanian' },
        { name: 'ru', label: 'Russian' },
        { name: 'sr', label: 'Serbian' },
        { name: 'zh-CN', label: 'Simplified Chinese' },
        { name: 'sd', label: 'Sindhi' },
        { name: 'si', label: 'Sinhala' },
        { name: 'sk', label: 'Slovak' },
        { name: 'sl', label: 'Slovenian' },
        { name: 'ckb', label: 'Sorani Kurdish' },
        { name: 'es', label: 'Spanish' },
        { name: 'sv', label: 'Swedish' },
        { name: 'tl', label: 'Tagalog' },
        { name: 'ta', label: 'Tamil' },
        { name: 'te', label: 'Telugu' },
        { name: 'th', label: 'Thai' },
        { name: 'bo', label: 'Tibetan' },
        { name: 'zh-TW', label: 'Traditional Chinese' },
        { name: 'tr', label: 'Turkish' },
        { name: 'uk', label: 'Ukrainian' },
        { name: 'ur', label: 'Urdu' },
        { name: 'ug', label: 'Uyghur' },
        { name: 'vi', label: 'Vietnamese' },
        { name: 'cy', label: 'Welsh' },
    ]},
];

export const fieldsWithValueOptions = ['has', 'is', 'retweetsOf', 'lang'];

const isRuleNode = (node: IXSifterQuery | IXSifterQueryRule): node is IXSifterQueryRule => {
    return (node as IXSifterQueryRule).field !== undefined;
}

const createEmptyGroup = (): IXSifterQuery => ({
    combinator: "and",
    isNot: false,
    rules: [],
});

const getDefaultOperator = (field: string): string => {
    if (fieldsWithValueOptions.includes(field)) {
        const fieldDef = fields.find(f => f.name === field);
        const operators = (fieldDef?.operators ?? []) as { name: string, label: string }[];
        return operators[0]?.name ?? "=";
    }
    return "=";
}

const createRule = (field: string): IXSifterQueryRule => ({
    field,
    operator: getDefaultOperator(field),
    value: "",
    combinator: "and",
    isNot: false,
});

const pathsEqual = (a: number[], b: number[]): boolean => {
    return a.length === b.length && a.every((value, index) => value === b[index]);
}

const rowKey = (path: number[], index: number): string => `${path.join(",")}:${index}`;

const updateGroupAtPath = (group: IXSifterQuery, path: number[], updater: (group: IXSifterQuery) => Partial<IXSifterQuery>): IXSifterQuery => {
    if (path.length === 0) {
        return { ...group, ...updater(group) };
    }
    const [index, ...rest] = path;
    const rules = [...(group.rules ?? [])];
    rules[index] = updateGroupAtPath(rules[index] as IXSifterQuery, rest, updater);
    return { ...group, rules };
}

// true when `path` refers to `ancestor` itself, or to something nested inside it
const isPathInsideOrEqual = (path: number[], ancestor: number[]): boolean => {
    return path.length >= ancestor.length && ancestor.every((value, index) => path[index] === value);
}

const removeNodeAtPath = (root: IXSifterQuery, path: number[], index: number): { tree: IXSifterQuery, removed: IXSifterQuery | IXSifterQueryRule } => {
    let removed: IXSifterQuery | IXSifterQueryRule;
    const tree = updateGroupAtPath(root, path, (group) => {
        const rules = [...(group.rules ?? [])];
        removed = rules[index];
        rules.splice(index, 1);
        return { rules };
    });
    return { tree, removed: removed! };
}

const insertNodeAtPath = (root: IXSifterQuery, path: number[], index: number, node: IXSifterQuery | IXSifterQueryRule): IXSifterQuery => {
    return updateGroupAtPath(root, path, (group) => {
        const rules = [...(group.rules ?? [])];
        rules.splice(index, 0, node);
        return { rules };
    });
}

// a removal at (removalPath, removalIndex) shifts every later sibling index down by one within
// that same array - reindex any path that runs through that array so it still points at the
// same logical node/slot after the removal.
const adjustPathAfterRemoval = (path: number[], removalPath: number[], removalIndex: number): number[] => {
    if (path.length <= removalPath.length || !pathsEqual(path.slice(0, removalPath.length), removalPath)) {
        return path;
    }
    const affectedIndex = path[removalPath.length];
    if (affectedIndex <= removalIndex) {
        return path;
    }
    const adjusted = [...path];
    adjusted[removalPath.length] = affectedIndex - 1;
    return adjusted;
}

const adjustIndexAfterRemoval = (destPath: number[], destIndex: number, removalPath: number[], removalIndex: number): number => {
    if (!pathsEqual(destPath, removalPath)) {
        return destIndex;
    }
    return destIndex > removalIndex ? destIndex - 1 : destIndex;
}

const moveNode = (root: IXSifterQueryRoot, sourcePath: number[], sourceIndex: number, destPath: number[], destIndex: number): IXSifterQueryRoot => {
    const { tree, removed } = removeNodeAtPath(root, sourcePath, sourceIndex);
    const adjustedDestPath = adjustPathAfterRemoval(destPath, sourcePath, sourceIndex);
    const adjustedDestIndex = adjustIndexAfterRemoval(destPath, destIndex, sourcePath, sourceIndex);
    return insertNodeAtPath(tree, adjustedDestPath, adjustedDestIndex, removed) as IXSifterQueryRoot;
}

interface IQueryBuilderComponentProps {
    existingQuery: IXSifterQueryRoot | null;
    onQueryChanged: (query: IXSifterQueryRoot) => void;
    disabled?: boolean;
}

const getInitialQuery = (existingQuery: IXSifterQueryRoot | null): IXSifterQueryRoot => {
    if (!existingQuery) {
        return { combinator: "and", isNot: false, rules: [createRule("keyword")] };
    }
    return checkStripNoRetweets(existingQuery) as IXSifterQueryRoot;
}

const QueryBuilderComponent: React.FC<IQueryBuilderComponentProps> = (props: IQueryBuilderComponentProps) => {
    const [currentQuery, setCurrentQuery] = useState<IXSifterQueryRoot>(() => getInitialQuery(props.existingQuery));
    const [filterMenuState, setFilterMenuState] = useState<{ anchorEl: HTMLElement, path: number[] } | null>(null);
    const [draggedNode, setDraggedNode] = useState<{ path: number[], index: number } | null>(null);
    const [dropTarget, setDropTarget] = useState<{ path: number[], index: number } | null>(null);
    const rowElements = useRef<Map<string, HTMLElement>>(new Map());

    useEffect(() => {
        props.onQueryChanged(currentQuery);
    }, [currentQuery, props.onQueryChanged]);

    const selectSx = { "& .MuiSelect-select": { paddingTop: "2px", paddingBottom: "2px" } };
    const fieldSelectSx = { ...selectSx, width: 150, "& .MuiSelect-select": { ...selectSx["& .MuiSelect-select"], overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } };
    const deleteButtonSx = { "&:hover": { color: "error.main" } };

    const updateGroup = (path: number[], updater: (group: IXSifterQuery) => Partial<IXSifterQuery>) => {
        setCurrentQuery(prev => updateGroupAtPath(prev, path, updater) as IXSifterQueryRoot);
    }

    const updateRuleAtIndex = (path: number[], ruleIndex: number, updates: Partial<IXSifterQueryRule>) => {
        updateGroup(path, (group) => {
            const rules = [...(group.rules ?? [])];
            rules[ruleIndex] = { ...(rules[ruleIndex] as IXSifterQueryRule), ...updates };
            return { rules };
        });
    }

    const onFilterButtonClick = (path: number[]) => (event: React.MouseEvent<HTMLButtonElement>) => {
        setFilterMenuState({ anchorEl: event.currentTarget, path });
    }

    const onFilterMenuClose = () => {
        setFilterMenuState(null);
    }

    const onFilterFieldSelected = (fieldName: string) => {
        if (!filterMenuState) {
            return;
        }
        updateGroup(filterMenuState.path, (group) => ({ rules: [...(group.rules ?? []), createRule(fieldName)] }));
        setFilterMenuState(null);
    }

    const onGroupButtonClick = (path: number[]) => () => {
        updateGroup(path, (group) => ({ rules: [...(group.rules ?? []), createEmptyGroup()] }));
    }

    const onDeleteNode = (path: number[], index: number) => {
        updateGroup(path, (group) => {
            const rules = [...(group.rules ?? [])];
            rules.splice(index, 1);
            return { rules };
        });
    }

    const onDragHandleDragStart = (path: number[], index: number) => (event: React.DragEvent) => {
        event.dataTransfer.effectAllowed = "move";

        const rowElement = rowElements.current.get(rowKey(path, index));
        if (rowElement) {
            const rowRect = rowElement.getBoundingClientRect();
            event.dataTransfer.setDragImage(rowElement, event.clientX - rowRect.left, event.clientY - rowRect.top);
        }

        // deferred on purpose: setting this synchronously re-renders immediately, which mounts the
        // "drop at end of list" target below - a new DOM sibling inserted right after whichever row
        // is currently the last in its list. When that row is the one being dragged, Chrome's native
        // drag session breaks on a DOM mutation landing right next to the drag source in the same
        // tick as dragstart, and the gesture silently hangs (dragstart fires, nothing after it does).
        // Pushing the state update to the next tick lets the native dragstart dispatch finish first.
        setTimeout(() => setDraggedNode({ path, index }), 0);
    }

    const onDragHandleDragEnd = () => {
        setDraggedNode(null);
        setDropTarget(null);
    }

    const canDropAt = (path: number[], index: number): boolean => {
        if (props.disabled || !draggedNode) {
            return false;
        }
        if (pathsEqual(draggedNode.path, path) && draggedNode.index === index) {
            // dropping a node onto its own current slot is a no-op
            return false;
        }
        // a group can't be dropped into itself or into one of its own descendants
        const draggedNodePath = [...draggedNode.path, draggedNode.index];
        return !isPathInsideOrEqual(path, draggedNodePath);
    }

    const onRowDragOver = (path: number[], index: number) => (event: React.DragEvent) => {
        if (!canDropAt(path, index)) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        setDropTarget({ path, index });
    }

    const onRowDrop = (path: number[], index: number) => (event: React.DragEvent) => {
        if (!draggedNode || !canDropAt(path, index)) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        const { path: sourcePath, index: sourceIndex } = draggedNode;
        setCurrentQuery(prev => moveNode(prev, sourcePath, sourceIndex, path, index));
        setDraggedNode(null);
        setDropTarget(null);
    }

    const isDropTarget = (path: number[], index: number): boolean => {
        return !!dropTarget && pathsEqual(dropTarget.path, path) && dropTarget.index === index;
    }

    const renderFilterValueField = (path: number[], ruleIndex: number, rule: IXSifterQueryRule) => {
        if (fieldsWithValueOptions.includes(rule.field)) {
            const currentField = fields.find(field => field.name === rule.field);
            const operators = (currentField?.operators ?? []) as { name: string, label: string }[];

            return (
                <Select
                    variant="standard"
                    value={rule.operator}
                    disabled={props.disabled}
                    onChange={(event: SelectChangeEvent) => updateRuleAtIndex(path, ruleIndex, { operator: event.target.value })}
                    sx={selectSx}
                >
                    {operators.map(operator => <MenuItem key={operator.name} value={operator.name}>{operator.label}</MenuItem>)}
                </Select>
            );
        }

        return (
            <TextField
                variant="standard"
                value={rule.value ?? ""}
                disabled={props.disabled}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    const newValue = rule.field === "phrase" ? event.target.value : event.target.value.replace(/[^\p{L}\p{N}\p{Emoji}]/gu, "");
                    updateRuleAtIndex(path, ruleIndex, { value: newValue });
                }}
            />
        );
    }

    const renderRuleItem = (path: number[], ruleIndex: number, rule: IXSifterQueryRule) => {
        return (
        <Stack
            key={ruleIndex}
            direction="row"
            spacing={2}
            sx={{ alignItems: "flex-end" }}
            ref={(el: HTMLElement | null) => { if (el) { rowElements.current.set(rowKey(path, ruleIndex), el); } }}
        >
            <Box
                draggable={!props.disabled}
                onDragStart={onDragHandleDragStart(path, ruleIndex)}
                onDragEnd={onDragHandleDragEnd}
                sx={{ display: "flex", alignItems: "center", cursor: props.disabled ? "default" : "grab" }}
            >
                <DragIndicatorIcon fontSize="small" />
            </Box>
            <SwitchButton disabled={props.disabled} choiceOne="AND" choiceTwo="OR" value={rule.combinator === "or"} onChange={(value) => updateRuleAtIndex(path, ruleIndex, { combinator: value ? "or" : "and" })} />
            <SwitchButton disabled={props.disabled} choiceOne="IS" choiceTwo="IS NOT" value={!!rule.isNot} onChange={(value) => updateRuleAtIndex(path, ruleIndex, { isNot: value })} />
            <Select
                variant="standard"
                value={rule.field}
                disabled={props.disabled}
                onChange={(event: SelectChangeEvent) => {
                    const newField = event.target.value;
                    const newValue = fieldsWithValueOptions.includes(newField) ? "" : rule.value;
                    updateRuleAtIndex(path, ruleIndex, { field: newField, operator: getDefaultOperator(newField), value: newValue });
                }}
                sx={fieldSelectSx}
            >
                {fields.map(field => <MenuItem key={field.name} value={field.name}>{field.label}</MenuItem>)}
            </Select>
            {renderFilterValueField(path, ruleIndex, rule)}
            <IconButton disabled={props.disabled} onClick={() => onDeleteNode(path, ruleIndex)} sx={deleteButtonSx}>
                <DeleteForeverIcon sx={{ fontSize: "1.5rem" }} />
            </IconButton>
        </Stack>
        );
    }

    const renderGroupItem = (path: number[], groupIndex: number, group: IXSifterQuery) => {
        const groupPath = [...path, groupIndex];
        return (
            <Stack
                key={groupIndex}
                direction="column"
                sx={{ marginTop: "14px" }}
                ref={(el: HTMLElement | null) => { if (el) { rowElements.current.set(rowKey(path, groupIndex), el); } }}
            >
                {/* a native <fieldset>/<legend> here was found to disrupt drag-and-drop on later
                    siblings (fieldset has special, inconsistent browser layout/hit-testing behavior
                    once its legend holds interactive content instead of plain text) - this plain Box
                    reproduces the same "label cut into the border" look without it. */}
                <Box sx={{ position: "relative", border: "1px solid", borderColor: "divider", borderRadius: 1, padding: 2 }}>
                    <Stack
                        direction="row"
                        spacing={2}
                        sx={{
                            position: "absolute",
                            top: 0,
                            left: 16,
                            transform: "translateY(-50%)",
                            backgroundColor: "#eeeeee",
                            paddingX: 1,
                            alignItems: "center",
                        }}
                    >
                        <Box
                            draggable={!props.disabled}
                            onDragStart={onDragHandleDragStart(path, groupIndex)}
                            onDragEnd={onDragHandleDragEnd}
                            sx={{ display: "flex", alignItems: "center", cursor: props.disabled ? "default" : "grab" }}
                        >
                            <DragIndicatorIcon fontSize="small" />
                        </Box>
                        <Box component="span" sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                            group
                        </Box>
                        <SwitchButton disabled={props.disabled} choiceOne="AND" choiceTwo="OR" value={group.combinator === "or"} onChange={(value) => updateGroup(groupPath, () => ({ combinator: value ? "or" : "and" }))} />
                        <SwitchButton disabled={props.disabled} choiceOne="IS" choiceTwo="IS NOT" value={!!group.isNot} onChange={(value) => updateGroup(groupPath, () => ({ isNot: value }))} />
                        <IconButton disabled={props.disabled} onClick={() => onDeleteNode(path, groupIndex)} sx={deleteButtonSx}>
                            <DeleteForeverIcon sx={{ fontSize: "1.5rem" }} />
                        </IconButton>
                    </Stack>
                    {renderQueryLevel(groupPath, group)}
                </Box>
            </Stack>
        );
    }

    const renderFilterOrGroupButtons = (path: number[]) => {
        const isMenuOpenForThisPath = !!filterMenuState && pathsEqual(filterMenuState.path, path);

        return (
            <Stack direction="row" spacing={2}>
                <Button variant="outlined" startIcon={<AddIcon />} disabled={props.disabled} onClick={onFilterButtonClick(path)}>
                    Filter
                </Button>
                <Menu anchorEl={isMenuOpenForThisPath ? filterMenuState.anchorEl : null} open={isMenuOpenForThisPath} onClose={onFilterMenuClose}>
                    {fields.map(field => (
                        <MenuItem key={field.name} onClick={() => onFilterFieldSelected(field.name)}>
                            {field.label}
                        </MenuItem>
                    ))}
                </Menu>
                <Button variant="outlined" startIcon={<AddIcon />} disabled={props.disabled} onClick={onGroupButtonClick(path)}>
                    Group
                </Button>
            </Stack>
        );
    }

    const renderQueryLevel = (path: number[], group: IXSifterQuery) => {
        const rules = group.rules ?? [];

        return (
            <Stack direction="column" spacing={2}>
                {rules.map((node, index) => (
                    <Box
                        key={index}
                        onDragOver={onRowDragOver(path, index)}
                        onDrop={onRowDrop(path, index)}
                    >
                        <Box sx={{ height: isDropTarget(path, index) ? "24px" : 0, display: "flex", alignItems: "center", transition: "height 0.15s ease" }}>
                            {isDropTarget(path, index) && (
                                <Box sx={{ width: "100%", borderTop: "1px dashed", borderColor: "secondary.main" }} />
                            )}
                        </Box>
                        {isRuleNode(node)
                            ? renderRuleItem(path, index, node)
                            : renderGroupItem(path, index, node)}
                    </Box>
                ))}
                {draggedNode && (
                    // lets a dragged filter/group be dropped at the end of this group - the only
                    // drop target available when the group is empty
                    <Box
                        onDragOver={onRowDragOver(path, rules.length)}
                        onDrop={onRowDrop(path, rules.length)}
                    >
                        <Box sx={{ height: isDropTarget(path, rules.length) ? "24px" : "8px", display: "flex", alignItems: "center", transition: "height 0.15s ease" }}>
                            {isDropTarget(path, rules.length) && (
                                <Box sx={{ width: "100%", borderTop: "1px dashed", borderColor: "secondary.main" }} />
                            )}
                        </Box>
                    </Box>
                )}
                {renderFilterOrGroupButtons(path)}
            </Stack>
        );
    }

    return (
        <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: "8px", backgroundColor: "#eeeeee", padding: 2, opacity: props.disabled ? 0.6 : 1 }}>
            {renderQueryLevel([], currentQuery)}
        </Box>
    )
}

export default QueryBuilderComponent;
